// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import classNames from 'classnames/bind';
import { getComparative } from '../utils/comparative';

import style from './Comparative.scss';
import buttonStyle from '../../../assets/styles/button.scss';
import boxStyle from '../../../assets/styles/box.scss';
import avatarStyle from '../../../assets/styles/avatar.scss'
import icons from '../../../assets/styles/icons.scss';
import qempoComputerImg from '../../../assets/img/comparative/qempo-computer.png';
import qempologo from '../../../assets/img/logoq-primary.png';

import type { Comparative as ComparativeType, Answer, Feature, Testimony } from '../utils/comparative';
import type { LocaleState } from '../../locale/duck';

type Props = {
  rivalry: string;
  locale?: LocaleState;
};

const settings = {
  dots: true,
  infinite: false,
  speed: 500,
  initialSlide: 0,
  slidesToShow: 1,
  slidesToScroll: 1,
  dotsClass: classNames('slick-dots', style.comparativeDots),
};

export default class Comparative extends Component<Props, void> {
  rivalry: ComparativeType;
  messages: { [key: string]: string };
  domainTitle: { [key: string]: string } | string;
  domainDescription: { [key: string]: string } | string;

  constructor(props: Props) {
    super(props);
    this.rivalry = getComparative(props.rivalry);
    this.domainTitle = this.rivalry.meta.title;
    if (typeof this.rivalry.meta.title !== 'string' && props.locale) {
      this.domainTitle = this.rivalry.meta.title[props.locale.domainCountry || 'US'];
    }
    this.domainDescription = this.rivalry.meta.description;
    if (typeof this.rivalry.meta.description !== 'string' && props.locale) {
      this.domainDescription = this.rivalry.meta.description[props.locale.domainCountry || 'US'];
    }
    this.messages = defineMessages({
      title: {
        id: this.domainTitle,
      },
      description: {
        id: this.domainDescription,
      },
    });
  }

  buildFeatureItems() {
    const {rivalry} = this.props;
    
    const isT = rivalry === 'tiendaMia';
    
    return this.rivalry.features.map((item: Feature, n: number) => (
      <article key={n} className={style.feature}>
        <FormattedMessage id={item.label}>{
          text => <p>{text}</p>
        }</FormattedMessage>
        <section className={style.qualification}>
          {typeof item.qempo === 'string' ? (
            <FormattedMessage id={item.qempo}>{
              text => <section className={style.qempo}>{text}</section>
            }</FormattedMessage>) : (
            <section className={style.qempo}>
              <div className={classNames(style.circle, style.green)}><i className={icons.iconCheckMedium}/></div>
            </section>)}
            {typeof item.rivalry === 'string' ? (
              <FormattedMessage id={item.rivalry}>{
                text => <section className={classNames(style.rivalry, {[style.hidden]: isT})}>{text}</section>
              }</FormattedMessage>) : (
              <section className={classNames(style.rivalry, {[style.hidden]: isT})}>
                <div className={classNames(style.circle, style.red)}><i className={icons.iconCloseMedium}/></div>
              </section>)}
        </section>
      </article>)
    )
  };

  buildAnswerItems() {
    return this.rivalry.answers.map((item: Answer, n: number) => (
      <article key={n} className={classNames(style.row, { [style.reverse]: n % 2 !== 0 })}>
        <section className={style.answer}>
          <FormattedMessage id={item.title}>{
            text => <h2>{text}</h2>
          }</FormattedMessage>
          <FormattedMessage id={item.content}>{
            text => <p>{text}</p>
          }</FormattedMessage>
        </section>
        <section className={style.image}>
          <img src={item.image} alt={item.title}/>
        </section>
      </article>)
    )
  }

  buildAnswersCarouselItems() {
    return this.rivalry.answers.map((item: Answer, n: number) => (
      <article key={n}>
        <img src={item.image} alt={item.title}/>
        <FormattedMessage id={item.title}>{
          text => <h2>{text}</h2>
        }</FormattedMessage>
        <FormattedMessage id={item.content}>{
          text => <p>{text}</p>
        }</FormattedMessage>
      </article>)
    )
  }

  buildTestimoniesItems() {
    return this.rivalry.testimonies.map((item: Testimony, n: number) => (
      <section key={n} className={classNames(boxStyle.box, style.box)}>
        <section className={style.user}>
          <img className={classNames(avatarStyle.avatar)} src={item.image} alt={item.autor}/>
          <section className={style.titles}>
            <p className={style.names}>{item.autor}</p>
            <p className={style.location}>{item.location}</p>
          </section>
        </section>
        <FormattedMessage id={item.testimony}>{
          text => <p className={style.content}>{text}</p>
        }</FormattedMessage>
      </section>)
    )
  }

  render() {
    const {rivalry} = this.props;
    
    const isT = rivalry === 'tiendaMia';
    
    return [
      <Helmet key="helmet">
        <title>{this.context.intl.formatMessage(this.messages.title)}</title>
        <meta name="description" content={this.context.intl.formatMessage(this.messages.description)}/>
      </Helmet>,
      <section key="banner" className={style.banner}>
        <FormattedMessage id={this.domainTitle}>{
          text => <h1 className={classNames({[style.hidden]: isT})}>{text}</h1>
        }</FormattedMessage>
        <FormattedMessage id={this.rivalry.short}>{
          text => <p>{text}</p>
        }</FormattedMessage>
        <img src={qempoComputerImg} alt="Qempo"/>
      </section>,
      <section key="comparativeWrap" className={style.comparativeWrap}>
        <FormattedMessage id={this.rivalry.subtitle2}>{
          text => <h2>{text}</h2>
        }</FormattedMessage>
        <section className={style.header}>
          <p/>
          <section className={style.marks}>
            <section className={style.qempo}>
              <img src={qempologo} alt="qempo"/>
            </section>
            <section className={classNames(style.rivalry, {[style.hidden]: isT})}>
              {this.rivalry.rivalry}
            </section>
          </section>
        </section>
        <section className={style.features}>
          {this.buildFeatureItems()}
        </section>
      </section>,
      <section key="answersWrap" className={style.answersWrap}>
        <section className={style.answers}>
          <FormattedMessage id={this.rivalry.subtitle2}>{
            text => <h2>{text}</h2>
          }</FormattedMessage>
          {this.buildAnswerItems()}
          <section className={style.carousel}>
            <Slider {...settings}>
              {this.buildAnswersCarouselItems()}
            </Slider>
          </section>
        </section>
      </section>,
      <section key="bannerBottom" className={style.bannerBottomWrap}>
        <section className={style.bannerBottom}>
          <i className={icons.symbolChat}/>
          <FormattedMessage id="comparative.bottom.subtitle">{
            text => <h2>{text}</h2>
          }</FormattedMessage>
        </section>
        <div className={style.arrowDown}/>
      </section>,
      <section key="testimoniesWrap" className={style.testimoniesWrap}>
        <section className={style.testimonies}>
          {this.buildTestimoniesItems()}
        </section>
      </section>,
      <FormattedMessage key="button" id="comparative.bottom.button">{
        text => <Link className={classNames(buttonStyle.btn, buttonStyle.lightBlue, style.comparativeBtn)} to="/">
          {text}</Link>
      }</FormattedMessage>
    ];
  }
}

Comparative.propTypes = {
  rivalry: PropTypes.string.isRequired,
  locale: PropTypes.object,
};

Comparative.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/comparative/components/Comparative.jsx